import React, { FC, useEffect, useState, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { OnDemandValidationRequestModalPresenter } from "./OnDemandValidationRequestModal.presenter";
import validationSchema, {
  OnDemandValidationFormData,
} from "./validation.schema";
import { Program } from "@/domain/programs.interface";
import { useGetPrograms } from "@/hooks/usePrograms";
import { useUpdateProgram } from "@/hooks/management/usePrograms.manage";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ProgramStatusEnum } from "@/domain/programs.enum";
import AlertDialog from "@/components/AlertDialog";
import { getDateOnly } from "@/utils/dateUtils";

export interface OnDemandValidationRequestModalContainerProps {
  open: boolean;
  onClose: () => void;
}

export const OnDemandValidationRequestModalContainer: FC<
  OnDemandValidationRequestModalContainerProps
> = ({ open, onClose }) => {
  const { showSnackBar } = useFeedBack();
  const queryClient = useQueryClient();

  // Fetch programs
  const { programs: allPrograms, isLoadingPrograms } = useGetPrograms({
    sortType: "asc",
  });

  // Filter non-deleted programs and sort alphabetically
  const programs = useMemo(() => {
    if (!allPrograms) return [];
    return allPrograms
      .filter((p) => p.status === ProgramStatusEnum.ACTIVE)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allPrograms]);

  // Mutations
  const updateProgram = useUpdateProgram();

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [pendingProgramId, setPendingProgramId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Form setup with dynamic validation based on selected program
  const methods = useForm<OnDemandValidationFormData>({
    resolver: yupResolver(
      validationSchema(selectedProgram?.parameters?.programDays || 0),
    ),
    mode: "onChange",
    defaultValues: {
      programId: "",
      onDemandValidationDays: "",
    },
  });

  // Auto-select first program when programs load
  useEffect(() => {
    if (programs.length > 0 && !methods.getValues("programId") && open) {
      const firstProgram = programs[0];
      methods.setValue("programId", firstProgram._id);
      setSelectedProgram(firstProgram);
      loadProgramDays(firstProgram);
    }
  }, [programs, open]);

  // Load program days into the form
  const loadProgramDays = (program: Program) => {
    const days =
      program.configuration?.validationRequestSettings
        ?.onDemandValidationDays || [];
    const daysString = days.join(", ");
    methods.setValue("onDemandValidationDays", daysString);
    setIsDirty(false);
  };

  // Track form changes
  useEffect(() => {
    const subscription = methods.watch(() => {
      setIsDirty(true);
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  // Handle program change
  const handleProgramChange = (programId: string) => {
    if (isDirty && selectedProgram && selectedProgram._id !== programId) {
      // Show unsaved changes dialog
      setPendingProgramId(programId);
      setShowUnsavedChangesDialog(true);
    } else {
      // No unsaved changes, switch immediately
      switchProgram(programId);
    }
  };

  const switchProgram = (programId: string) => {
    const program = programs.find((p) => p._id === programId);
    if (program) {
      setSelectedProgram(program);
      loadProgramDays(program);
    }
  };

  const handleConfirmProgramSwitch = () => {
    if (pendingProgramId) {
      switchProgram(pendingProgramId);
      setPendingProgramId(null);
    }
    setShowUnsavedChangesDialog(false);
  };

  const handleCancelProgramSwitch = () => {
    // Revert the dropdown to the current program
    if (selectedProgram) {
      methods.setValue("programId", selectedProgram._id);
    }
    setPendingProgramId(null);
    setShowUnsavedChangesDialog(false);
  };

  // Handle submit
  const handleSubmit = async (data: OnDemandValidationFormData) => {
    if (!selectedProgram) return;

    setIsSubmitting(true);

    try {
      // Parse and deduplicate the days
      let daysArray: number[] = [];
      if (data.onDemandValidationDays.trim() !== "") {
        const parsedDays = data.onDemandValidationDays
          .split(",")
          .map((d) => parseInt(d.trim(), 10))
          .filter((d) => !isNaN(d));

        // Remove duplicates
        daysArray = Array.from(new Set(parsedDays));
      }

      // Build payload similar to edit program page
      const payload = {
        name: selectedProgram.name,
        status: selectedProgram.status,
        roleId: selectedProgram.roleId,
        additionalStaffRoles: selectedProgram.additionalStaffRoles,
        programType: selectedProgram.programType,
        maxParticipants: selectedProgram.maxParticipants,
        duration: selectedProgram.duration,
        budgetLimit: selectedProgram.budgetLimit,
        parameters: {
          ...selectedProgram.parameters,
          startDate:
            getDateOnly(selectedProgram.parameters?.startDate as any) || "",
          endDate: selectedProgram.parameters?.endDate
            ? getDateOnly(selectedProgram.parameters.endDate as any) || ""
            : undefined,
        },
        orgUnitIds: selectedProgram.orgUnitIds,
        configuration: {
          ...selectedProgram.configuration,
          validationRequestSettings: {
            ...selectedProgram.configuration?.validationRequestSettings,
            onDemandValidationDays: daysArray,
          },
        },
        events: selectedProgram.events,
      };

      // Update the program
      await updateProgram.mutateAsync({
        id: selectedProgram._id,
        data: payload,
      });

      // Invalidate queries to refresh stale data
      queryClient.invalidateQueries({ queryKey: ["get-programs"] });
      queryClient.invalidateQueries({
        queryKey: ["coyn-program-get", selectedProgram._id],
      });
      queryClient.invalidateQueries({
        queryKey: ["coyn-programs-manage-list"],
      });

      showSnackBar("The configuration has been saved.", "success");
      setIsDirty(false);
      onClose();
    } catch (error) {
      const err = error as ErrorResponse;
      const errorMessage =
        "There has been an inconvinient while attempting to save";
      showSnackBar(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      methods.reset();
      setSelectedProgram(null);
      setIsDirty(false);
      setPendingProgramId(null);
    }
  }, [open]);

  return (
    <>
      <FormProvider {...methods}>
        <OnDemandValidationRequestModalPresenter
          open={open}
          isSubmitting={isSubmitting}
          isLoadingPrograms={isLoadingPrograms}
          programs={programs}
          selectedProgram={selectedProgram}
          onClose={onClose}
          onSubmit={methods.handleSubmit(handleSubmit)}
          onProgramChange={handleProgramChange}
        />
      </FormProvider>

      {/* Unsaved changes confirmation dialog */}
      <AlertDialog
        open={showUnsavedChangesDialog}
        title="Unsaved Changes"
        description="The changes you made will not be saved. Do you want to continue?"
        confirmText="Continue"
        cancelText="Cancel"
        onConfirm={handleConfirmProgramSwitch}
        onCancel={handleCancelProgramSwitch}
      />
    </>
  );
};
